import { Timestamp } from "@bufbuild/protobuf";

import React, { useCallback, useContext, useEffect, useState } from "react";

import { Autorenew } from "@mui/icons-material";
import { VisibilityOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Link,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
} from "@mui/material";
import { Chip } from "@mui/material";
import clsx from "clsx";

import { BillingProvider } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_provider_pb";
import { SubscriptionState } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { ChargifyState } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import {
  RoleFlags,
  Subscription,
  SubscriptionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { PermissionsContext } from "../../../context/permissions-context";
import { getScUserEmail } from "../../../helpers/get-sc-user-email";
import { getScUserName } from "../../../helpers/get-sc-user-name";
import mainStyles from "../../../styles";
import { OrgDeletionEventDetail } from "../../../types/org-deletion-event-detail";
import ConfirmDialog from "../../shared/confirm-dialog";
import DisplayHeading from "../../shared/display-heading";
import GenericDialog from "../../shared/generic-dialog";
import MapOrgResultDetails from "../details-display-cards/map-org-result-details";
import CloneOrganisation from "./data-governance-actions/clone-organisation";
import DataLoaderDialog from "./data-governance-actions/data-loader/data-loader-dialog";
import DeleteOrganisation from "./data-governance-actions/delete-organisation";
import MapOrganisation from "./data-governance-actions/map-organisation";
import MigrateOrganisation from "./data-governance-actions/migrate-organisation";
import RestoreSites from "./site-actions/restore-sites";
import {
  deleteOrganisation,
  mapOrganisation,
  mapOrganisationStatus,
  updateInternalOrgFlags,
} from "api/endpoints/data-governance";
import { setExcludeOrgFromAnalytics } from "api/endpoints/organisation";
import {
  getWorkflowStatus,
  terminateSupportWorkflowExecution,
} from "api/endpoints/workflows";
import { callOptionsHeaders } from "api/helpers";
import ActionPermissionWrapper from "components/shared/action-permission-wrapper";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import { WorkflowStatus } from "constants/workflow";
import { SubDataContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { mapRegionCodeToRegionString } from "helpers/env-values-helper";
import { convertTimestampToDate } from "helpers/time";

interface DataGovernanceTabProps {
  roleId: string;
  orgName: string;
  numMembers: number;
  numInactiveMembers: number;
  regionCode: string;
  subscription: Subscription;
  roleFlags: RoleFlags;
}

const DataGovernanceTab = ({
  roleId,
  orgName,
  numMembers,
  numInactiveMembers,
  regionCode,
  subscription,
  roleFlags,
}: DataGovernanceTabProps) => {
  const orgDeleteReferenceKey = `delete_${mapRegionCodeToRegionString(
    regionCode,
  )}_${roleId}`;
  const demoDataLoaderWorkflowId = `BulkCreateDemoOrgData_${roleId}`;
  const { permissions, hasPermissions } = useContext(PermissionsContext);

  const classes = mainStyles();
  const theme = useTheme();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [orgDeletionReference, setOrgDeletionReference] = useState("");
  const [currentlyRunningDeleteWorkflow, setCurrentlyRunningDeleteWorkflow] =
    useState(false);
  const [dataLoaderWorkflowReference, setDataLoaderWorkflowReference] =
    useState("");
  const [dataLoaderStatusLoading, setDataLoaderStatusLoading] = useState(true);
  const [
    currentlyRunningDataLoaderWorkflow,
    setCurrentlyRunningDataLoaderWorkflow,
  ] = useState(false);
  const [orgDeletionRequest, setOrgDeletionRequest] = useState("");
  const [disableOrgDeletion, setDisableOrgDeletion] = useState(true);
  const [generatingDeleteJob, setGeneratingDeleteJob] = useState(false);
  const [scUserName, setScUserName] = useState("");
  const [scUserEmail, setScUserEmail] = useState("");
  const [isOrgActive] = useState(roleFlags.active);
  const [isTestAccount, setIsTestAccount] = useState(roleFlags.testAccount);
  const [isOrgExludeFromIA, setIsOrgExludeFromIA] = useState(
    roleFlags.excludeFromAnalytics,
  );
  const [disableAllSwitches, setDisableAllSwitches] = useState(false);
  const [switchSuccess, setSwitchSuccess] = useState(false);
  const [disableOrgMapping, setDisableOrgMapping] = useState(true);
  const [openMapDialog, setOpenMapDialog] = useState(false);
  const [orgMapRequest, setOrgMapRequest] = useState("");
  const [openMappingResult, setOpenMappingResult] = useState(false);
  const [mapState, setMapState] = useState("");
  const [mappingInProgess, setMappingInProgess] = useState(false);
  const [mappingResultReady, setMappingResultReady] = useState(false);
  const [mappingSummary, setMappingSummary] = useState<Array<[string, string]>>(
    [],
  );
  const [mappingIsLoading, setMappingIsLoading] = useState(true);
  const [summaryCloseTime, setSummaryCloseTime] = useState<Timestamp>();
  const [disableStartMapping, setDisableStartMapping] = useState(false);

  const [openDataLoaderDialog, setOpenDataLoaderDialog] = useState(false);

  const isPeriscopeAdmin = hasPermissions([
    ActionType.CREATE_GROUP,
    ActionType.ADD_ACTION,
  ]);

  const canCloneOrgs = hasPermissions([ActionType.CLONE_ORGANISATION]);
  const canCreateDemoOrgData = hasPermissions([
    ActionType.BULK_CREATE_DEMO_ORG_DATA,
  ]);

  const { showError } = useContext(SnackbarContext);
  const { subData } = useContext(SubDataContext);

  const handleGetWorkflowStatus = () => {
    getWorkflowStatus(
      { workflowId: orgDeleteReferenceKey, queryType: "delete" },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((workflowStatusResponse) => {
        if (
          workflowStatusResponse.executionInfo?.status ===
          WorkflowStatus.RUNNING
        ) {
          setCurrentlyRunningDeleteWorkflow(true);
          setOrgDeletionReference(orgDeleteReferenceKey);
        }
      })
      .catch(showError);
  };

  const handleGetDemoDataLoaderWorkflowStatus = () => {
    setDataLoaderStatusLoading(true);
    getWorkflowStatus(
      { workflowId: demoDataLoaderWorkflowId, queryType: "progress" },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((workflowStatusResponse) => {
        if (
          workflowStatusResponse.executionInfo?.status ===
          WorkflowStatus.RUNNING
        ) {
          setCurrentlyRunningDataLoaderWorkflow(true);
          setDataLoaderWorkflowReference(demoDataLoaderWorkflowId);
        } else {
          setCurrentlyRunningDataLoaderWorkflow(false);
          setDataLoaderWorkflowReference("");
        }
      })
      .catch(showError)
      .finally(() => {
        setDataLoaderStatusLoading(false);
      });
  };

  const handleTerminateWorkflowExecution = () => {
    setDataLoaderStatusLoading(true);
    terminateSupportWorkflowExecution(
      {
        workflowId: demoDataLoaderWorkflowId,
        reason: "manual termination",
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        handleGetDemoDataLoaderWorkflowStatus();
      })
      .catch(showError)
      .finally(() => {
        setDataLoaderStatusLoading(false);
      });
  };

  const handleDeleteOrganisation = () => {
    deleteOrganisation(
      {
        orgId: roleId,
        region: regionCode,
        employeeName: scUserName,
        employeeEmail: scUserEmail,
        deletionRequestReference: orgDeletionRequest,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((deletionRespObj) => {
        setOrgDeletionReference(deletionRespObj.jobUrl);
        const eventDetail: OrgDeletionEventDetail = {
          regionCode: regionCode,
          roleId: roleId,
        };
        window.dispatchEvent(
          new CustomEvent("org-deletion-event", { detail: eventDetail }),
        );
      })
      .catch(showError)
      .finally(() => {
        setGeneratingDeleteJob(false);
      });
  };

  const generateDataLoaderDisplayString = () => {
    if (currentlyRunningDataLoaderWorkflow) {
      return (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          data-testid="data-loader-workflow-reference-string"
        >
          <Grid item xs={9}>
            <p>
              Data loading is currently underway - Reference:{" "}
              {dataLoaderWorkflowReference}
            </p>
          </Grid>
          <Grid item xs={3} height={"100%"}>
            <Chip
              variant="outlined"
              size="small"
              data-testid="pill-status-refresh-data-loader-status"
              label="Refresh Status"
              color="info"
              onClick={() => handleGetDemoDataLoaderWorkflowStatus()}
            />
          </Grid>
        </Grid>
      );
    }
    return (
      <>
        Populate organisation with dummy data (inspections, actions, issues){" "}
        <br />{" "}
        <Link
          color="inherit"
          target="_blank"
          rel="noopener noreferrer"
          href="https://safetyculture.atlassian.net/wiki/spaces/PMM/pages/3955622288/Automated+Demo+Loader+Project+Tracker"
        >
          <strong>
            Please refer to the documentation for more information.
          </strong>
        </Link>
      </>
    );
  };

  const generationDeletionReferenceString = () => {
    if (currentlyRunningDeleteWorkflow) {
      return (
        <Grid
          container
          justifyContent="center"
          data-testid="temporal-workflow-reference-string"
        >
          <p>Temporal Reference: {orgDeletionReference}</p>
        </Grid>
      );
    }
    if (isPaidOrg) {
      return "Paid organisations cannot be deleted";
    } else {
      return generatingDeleteJob ? (
        <Grid container justifyContent="center">
          <CircularProgress
            color="primary"
            variant="indeterminate"
            thickness={4}
            size={30}
          />
        </Grid>
      ) : (
        <Link
          href={orgDeletionReference}
          target="_blank"
          underline="hover"
          data-anchor="delete-org-reference-string"
        >
          {orgDeletionReference}
        </Link>
      );
    }
  };

  const handleExcludeFromIAChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDisableAllSwitches(true);
    const isExcludeIA = event.target.checked;
    setExcludeOrgFromAnalytics(
      {
        orgId: roleId,
        excludeFromAnalytics: isExcludeIA,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        setIsOrgExludeFromIA(isExcludeIA);
        setSwitchSuccess(true);
      })
      .catch(showError)
      .finally(() => {
        setDisableAllSwitches(false);
      });
  };

  const handleTestAccountChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDisableAllSwitches(true);
    let orgExcludeFromIA = isOrgExludeFromIA;
    const testAccount = event.target.checked;
    if (testAccount) {
      orgExcludeFromIA = testAccount;
    }

    updateInternalOrgFlags(
      {
        orgId: roleId,
        isActive: isOrgActive,
        isExcludeFromIA: orgExcludeFromIA,
        isTestAccount: testAccount,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        setIsTestAccount(testAccount);
        setIsOrgExludeFromIA(orgExcludeFromIA);
        setSwitchSuccess(true);
      })
      .catch(showError)
      .finally(() => {
        setDisableAllSwitches(false);
      });
  };

  const checkMappingStatus = useCallback(() => {
    setMappingIsLoading(true);
    setDisableStartMapping(true);
    mapOrganisationStatus(
      {
        orgId: roleId,
        sourceRegion: regionCode,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((resp) => {
        setDisableStartMapping(false);
        switch (resp.executionInfo?.status) {
          case WorkflowStatus.RUNNING:
            const options: Intl.DateTimeFormatOptions = {
              year: "numeric",
              month: "short",
              day: "2-digit",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
              timeZoneName: "short",
            };
            let formattedTime = null;
            if (resp.executionInfo?.startTime) {
              formattedTime = convertTimestampToDate(
                resp.executionInfo?.startTime,
              ).toLocaleString("en-AU", options);
            }
            const message =
              formattedTime === ""
                ? "Mapping is in progress..."
                : "Mapping started at " + formattedTime + "...";
            setMapState(message);
            setDisableStartMapping(true);
            setMappingInProgess(true);
            setMappingResultReady(false);
            break;
          case WorkflowStatus.COMPLETED:
            setDisableStartMapping(false);
            setMappingSummary(Object.entries(resp.summary));
            setMapState("Mapping data is available");
            setMappingInProgess(false);
            setMappingResultReady(true);
            break;
          default:
            setMapState("No mapping data available");
        }
        setSummaryCloseTime(resp.executionInfo?.closeTime);
      })
      .catch(showError)
      .finally(() => setMappingIsLoading(false));
  }, [orgMapRequest, regionCode, roleId, showError]);

  const handleMapOrganisation = () => {
    setMappingIsLoading(true);
    setDisableStartMapping(true);
    mapOrganisation(
      {
        orgId: roleId,
        sourceRegion: regionCode,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        checkMappingStatus();
      })
      .catch(showError);
  };

  const isZuoraOrg =
    subData?.subscription?.billingProvider === BillingProvider.ZUORA;

  const isPaidOrg =
    subscription &&
    subscription.type !== SubscriptionType.FREE &&
    subscription.type !== SubscriptionType.FREE_ORG &&
    (isZuoraOrg
      ? subData?.subscription?.state !== SubscriptionState.CANCELED
      : subscription.chargify?.state !== ChargifyState.CANCELED);

  useEffect(() => {
    getScUserName().then((name) => setScUserName(name));
    getScUserEmail().then((email) => setScUserEmail(email));
    handleGetWorkflowStatus();
    checkMappingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkMappingStatus]);

  useEffect(() => {
    handleGetDemoDataLoaderWorkflowStatus();
  }, []);

  return (
    <Grid container>
      <Grid item xs={12} sx={classes.paddingTopSmall}>
        <DisplayHeading>Data Governance</DisplayHeading>
      </Grid>
      <Grid item xs={12}>
        <Box
          style={{
            borderColor: theme.palette.divider,
            borderWidth: "1px",
            borderStyle: "solid",
            borderRadius: "5px",
            margin: "20px",
          }}
        >
          <Table size="medium">
            <TableBody>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                  style={{ width: "20%" }}
                >
                  Delete Organisation
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                  style={{ fontWeight: "normal", width: "50%" }}
                >
                  {generationDeletionReferenceString()}
                </TableCell>
                <TableCell
                  className={clsx(classes.tableCell, classes.rightButtonCell)}
                  align="center"
                  width="10%"
                >
                  <Button
                    className={clsx(
                      classes.spaceTopSmall,
                      classes.spaceRightMedium,
                    )}
                    variant="contained"
                    color="secondary"
                    disabled={
                      currentlyRunningDeleteWorkflow ||
                      orgDeletionReference.length !== 0 ||
                      !hasPermissions([ActionType.DELETE_ORGANISATION]) ||
                      isPaidOrg
                    }
                    fullWidth
                    onClick={() => {
                      setOpenDeleteDialog(true);
                    }}
                    data-testid="delete-organisation-btn"
                    startIcon={
                      permissions.isPermissionsLoading ? (
                        <Autorenew className="spin-1s" />
                      ) : null
                    }
                  >
                    Permanently Delete
                  </Button>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                  style={{ width: "20%" }}
                >
                  Restore Deleted Sites
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                  style={{ fontWeight: "normal", width: "50%" }}
                >
                  Restore sites that have been deleted
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                  style={{ fontWeight: "normal", width: "50%" }}
                >
                  <RestoreSites />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                  style={{ width: "20%" }}
                >
                  Exclude from Analytics
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                  style={{ fontWeight: "normal", width: "50%" }}
                >
                  Marks the organisation to not be included in the Internal
                  Analytics pipeline
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                  style={{ fontWeight: "normal", width: "50%" }}
                >
                  <FormControlLabel
                    control={
                      <ActionPermissionWrapper
                        action={ActionType.SET_EXCLUDE_ORG_FROM_ANALYTICS}
                      >
                        <Switch
                          checked={isOrgExludeFromIA}
                          onChange={handleExcludeFromIAChange}
                          disabled={disableAllSwitches}
                          color="primary"
                          data-testid="is-exclude-switch"
                        />
                      </ActionPermissionWrapper>
                    }
                    label=""
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                  style={{ width: "20%" }}
                >
                  Test Account
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                  style={{ fontWeight: "normal", width: "50%" }}
                >
                  Marks the organisation as a test account, this will also mark
                  it to be excluded from analytics
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                  style={{ fontWeight: "normal", width: "50%" }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isTestAccount}
                        onChange={handleTestAccountChange}
                        disabled={
                          !hasPermissions([ActionType.UPDATE_ORG_FLAGS]) ||
                          disableAllSwitches
                        }
                        color="primary"
                        data-testid="is-test-account-switch"
                      />
                    }
                    label=""
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                  style={{ width: "20%" }}
                >
                  Data Loader
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                  style={{ fontWeight: "normal", width: "50%" }}
                >
                  {generateDataLoaderDisplayString()}
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                  style={{ fontWeight: "normal", width: "50%" }}
                >
                  <Button
                    className={clsx(
                      classes.spaceTopSmall,
                      classes.spaceRightMedium,
                    )}
                    variant="contained"
                    color={
                      currentlyRunningDataLoaderWorkflow
                        ? "secondary"
                        : "primary"
                    }
                    fullWidth
                    disabled={
                      !isOrgExludeFromIA ||
                      !canCreateDemoOrgData ||
                      dataLoaderStatusLoading
                    }
                    onClick={() => {
                      if (currentlyRunningDataLoaderWorkflow) {
                        handleTerminateWorkflowExecution();
                      } else {
                        setOpenDataLoaderDialog(true);
                      }
                    }}
                    data-testid="load-test-data-btn"
                    startIcon={
                      permissions.isPermissionsLoading ||
                      dataLoaderStatusLoading ? (
                        <Autorenew className="spin-1s" />
                      ) : null
                    }
                  >
                    {currentlyRunningDataLoaderWorkflow
                      ? "Terminate demo data loading"
                      : "Load demo data"}
                  </Button>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                  style={{ width: "20%" }}
                >
                  Map Organisation
                </TableCell>

                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                  style={{ fontWeight: "normal", width: "50%" }}
                >
                  {mappingIsLoading ? (
                    <CircularProgress
                      color="primary"
                      variant="indeterminate"
                      thickness={4}
                      size={30}
                      data-testid="loading-icon"
                    />
                  ) : (
                    <>
                      <span
                        style={{ marginRight: "20px" }}
                        data-testid="map-state-display"
                      >
                        {mapState}
                      </span>

                      <Chip
                        variant="outlined"
                        size="small"
                        data-testid="pill-status-refresh-status"
                        disabled={false}
                        label="Refresh Status"
                        color="info"
                        style={{
                          color: "#178a02",
                          visibility: mappingInProgess ? "visible" : "hidden",
                        }}
                        onClick={() => checkMappingStatus()}
                      />
                      <Chip
                        variant="outlined"
                        size="small"
                        data-testid="pill-status-view-mapping-result"
                        disabled={false}
                        label="View mapping result"
                        color="info"
                        style={{
                          color: "#178a02",
                          visibility: mappingResultReady ? "visible" : "hidden",
                        }}
                        onClick={() => setOpenMappingResult(true)}
                        icon={
                          <VisibilityOutlined style={{ color: "#178a02" }} />
                        }
                      />
                    </>
                  )}
                </TableCell>
                <TableCell
                  className={clsx(classes.tableCell, classes.rightButtonCell)}
                  align="center"
                  width="10%"
                >
                  <Button
                    className={clsx(
                      classes.spaceTopSmall,
                      classes.spaceRightMedium,
                    )}
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={disableStartMapping}
                    onClick={() => {
                      setOpenMapDialog(true);
                    }}
                    data-testid="map-organisation-btn"
                    startIcon={
                      permissions.isPermissionsLoading ? (
                        <Autorenew className="spin-1s" />
                      ) : null
                    }
                  >
                    Start mapping
                  </Button>
                </TableCell>
              </TableRow>
              {isPeriscopeAdmin && (
                <MigrateOrganisation
                  scEmployeeEmail={scUserEmail}
                  scEmployeeName={scUserName}
                />
              )}
              {canCloneOrgs && (
                <CloneOrganisation
                  scEmployeeEmail={scUserEmail}
                  scEmployeeName={scUserName}
                />
              )}
            </TableBody>
          </Table>
        </Box>
      </Grid>
      <ConfirmDialog
        title="Delete Organisation"
        open={openDeleteDialog}
        setOpen={setOpenDeleteDialog}
        onConfirm={() => {
          setGeneratingDeleteJob(true);
          handleDeleteOrganisation();
          setOpenDeleteDialog(false);
        }}
        disabled={disableOrgDeletion}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Delete"
      >
        <DeleteOrganisation
          orgName={orgName}
          numInactiveMembers={numInactiveMembers}
          numMembers={numMembers}
          handleDisableOrgDeletion={(isDisabled) => {
            setDisableOrgDeletion(isDisabled);
          }}
          handleDeletionRequestChanged={(orgDeletionRequest) => {
            setOrgDeletionRequest(orgDeletionRequest);
          }}
        />
      </ConfirmDialog>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={switchSuccess}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setSwitchSuccess(false)}
        data-testid="switch-change-success"
      >
        <Alert severity="success" variant="filled">
          Org Internal Flag Successfully Changed
        </Alert>
      </Snackbar>
      <ConfirmDialog
        title="Map Organisation"
        open={openMapDialog}
        setOpen={setOpenMapDialog}
        onConfirm={() => {
          handleMapOrganisation();
          setOrgMapRequest("");
        }}
        disabled={disableOrgMapping}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Start"
        onCancel={() => {
          setOrgMapRequest("");
        }}
      >
        <MapOrganisation
          handleDisableOrgMapping={(
            isDisabled: boolean | ((prevState: boolean) => boolean),
          ) => {
            setDisableOrgMapping(isDisabled);
          }}
          handleMapRequestChanged={(request: React.SetStateAction<string>) => {
            setOrgMapRequest(request);
          }}
        />
      </ConfirmDialog>
      {canCreateDemoOrgData && (
        <DataLoaderDialog
          isDialogOpen={openDataLoaderDialog}
          handleDialogClose={() => {
            setOpenDataLoaderDialog(false);
            handleGetDemoDataLoaderWorkflowStatus();
          }}
        />
      )}
      <GenericDialog
        title="Mapping summary"
        width="md"
        open={openMappingResult}
        handleAckBtnClick={() => {
          setOpenMappingResult(false);
        }}
        handleDialogClose={() => {
          setOpenMappingResult(false);
        }}
        hasAckBtn={true}
        acknowledgeBtnLabel="OK"
      >
        <MapOrgResultDetails
          summary={mappingSummary}
          closeTime={
            summaryCloseTime ?? new Timestamp({ seconds: BigInt(0), nanos: 0 })
          }
        />
      </GenericDialog>{" "}
    </Grid>
  );
};

export default DataGovernanceTab;
